// src/lib/mui-theme.ts
import { createTheme, responsiveFontSizes, Theme, PaletteOptions } from '@mui/material/styles'
import React from 'react'

export type ThemeMode = 'light' | 'dark'

const mainColor = '#499656'
const lightMainColor = '#6BB378'
const darkMainColor = '#2E7B3B'

const lightPalette: PaletteOptions = {
  mode: 'light',
  primary: {
    main: mainColor,
    light: lightMainColor,
    dark: darkMainColor,
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#7B2E6C',
    light: '#9D4A8D',
    dark: '#5C1D50',
    contrastText: '#ffffff',
  },
  error: {
    main: '#f44336',
    light: '#e57373',
    dark: '#d32f2f',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#ff9800',
    light: '#ffb74d',
    dark: '#f57c00',
    contrastText: '#000000',
  },
  info: {
    main: '#0288d1',
    light: '#03a9f4',
    dark: '#01579b',
    contrastText: '#ffffff',
  },
  success: {
    main: mainColor,
    light: lightMainColor,
    dark: darkMainColor,
    contrastText: '#ffffff',
  },
  background: {
    default: '#f8f9fa',
    paper: '#ffffff',
  },
  text: {
    primary: 'rgba(0, 0, 0, 0.87)',
    secondary: 'rgba(0, 0, 0, 0.6)',
  },
}

// ダークモードのパレット設定
const darkPalette: PaletteOptions = {
  mode: 'dark',
  primary: {
    main: lightMainColor,
    light: '#8BC99C',
    dark: mainColor,
    contrastText: '#000000',
  },
  secondary: {
    main: '#CE93D8',
    light: '#F3E5F5',
    dark: '#AB47BC',
    contrastText: '#000000',
  },
  error: {
    main: '#f44336',
    light: '#e57373',
    dark: '#d32f2f',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#ff9800',
    light: '#ffb74d',
    dark: '#f57c00',
    contrastText: '#000000',
  },
  info: {
    main: '#29b6f6',
    light: '#4fc3f7',
    dark: '#0288d1',
    contrastText: '#000000',
  },
  success: {
    main: lightMainColor,
    light: '#8BC99C',
    dark: mainColor,
    contrastText: '#000000',
  },
  background: {
    default: '#121212',
    paper: '#1e1e1e',
  },
  text: {
    primary: 'rgba(255, 255, 255, 0.87)',
    secondary: 'rgba(255, 255, 255, 0.6)',
  },
}

export const createAppTheme = (mode: ThemeMode): Theme => {
  let theme = createTheme({
    palette: mode === 'light' ? lightPalette : darkPalette,
    typography: {
      fontFamily: [
        'Roboto',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Arial',
        'sans-serif',
      ].join(','),
      h1: {
        fontSize: '2.25rem',
        fontWeight: 700,
        lineHeight: 1.2,
      },
      h2: {
        fontSize: '1.8rem',
        fontWeight: 700,
        lineHeight: 1.3,
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h4: {
        fontSize: '1.3rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: '1.1rem',
        fontWeight: 500,
        lineHeight: 1.4,
      },
      h6: {
        fontSize: '0.9rem',
        fontWeight: 500,
        lineHeight: 1.4,
      },
      body1: {
        fontSize: '0.9rem',
        fontWeight: 400,
        lineHeight: 1.5,
      },
      body2: {
        fontSize: '0.825rem',
        fontWeight: 400,
        lineHeight: 1.5,
      },
      button: {
        fontSize: '0.825rem',
        fontWeight: 500,
        lineHeight: 1.75,
        textTransform: 'none',
      },
    },
    shape: {
      borderRadius: 8,
    },
    spacing: 8,
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 24, // ボタンの角丸を大きめに
            padding: '8px 16px', // 元のパディングに戻す
            fontSize: '0.875rem', // フォントサイズを元に戻す
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
            },
          },
          sizeSmall: {
            padding: '6px 12px',
            fontSize: '0.8125rem',
            height: '32px',
          },
          sizeMedium: {
            fontSize: '0.875rem',
            height: '36px',
          },
          sizeLarge: {
            fontSize: '0.9375rem',
            height: '44px',
          },
        },
        defaultProps: {
          disableElevation: true,
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow:
              mode === 'light' ? '0px 2px 8px rgba(0,0,0,0.1)' : '0px 2px 8px rgba(0,0,0,0.3)',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
            },
            '& .MuiInputBase-input': {
              fontSize: '0.9rem',
              padding: '8px 14px',
            },
            '& .MuiInputLabel-root': {
              fontSize: '0.9rem',
            },
          },
        },
        defaultProps: {
          size: 'small',
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: '0.9rem',
          },
          sizeSmall: {
            fontSize: '0.875rem',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&.MuiInputBase-sizeSmall': {
              minHeight: '36px',
            },
          },
          input: {
            padding: '8px 14px', // パディングを調整
            height: 'auto', // 自動調整
            '&.MuiInputBase-inputSizeSmall': {
              padding: '7px 12px', // smallサイズのパディング
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            fontSize: '0.9rem', // セレクトのテキストサイズ
            minHeight: '1.25rem', // 最小高さを設定
            padding: '8px 14px', // パディングを調整
            '&.MuiInputBase-inputSizeSmall': {
              padding: '7px 12px', // smallサイズのパディング
            },
          },
        },
        defaultProps: {
          size: 'small', // デフォルトでsmallサイズに
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: '0.9rem', // メニューアイテムのテキストサイズ
            minHeight: '36px', // 高さを調整
            padding: '6px 16px',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow:
              mode === 'light' ? '0 1px 3px rgba(0,0,0,0.12)' : '0 1px 3px rgba(0,0,0,0.3)',
            // AppBarの高さを調整
            minHeight: '48px',
            '& .MuiToolbar-root': {
              minHeight: '48px',
            },
          },
        },
        defaultProps: {
          color: 'primary',
          elevation: 0,
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            minHeight: '48px !important', // AppBarの高さに合わせる
            '@media (min-width: 600px)': {
              minHeight: '48px !important',
            },
          },
        },
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          root: {
            fontSize: '0.8rem', // パンくずのテキストサイズ
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '12px 16px', // パディングを少し小さく
            fontSize: '0.85rem', // テキストサイズ
          },
          head: {
            fontWeight: 600,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            fontSize: '0.75rem', // チップのテキストサイズ
          },
        },
      },
      MuiFormControl: {
        defaultProps: {
          size: 'small', // デフォルトでsmallサイズに
        },
      },
    },
  })

  // レスポンシブフォントサイズの適用
  theme = responsiveFontSizes(theme)

  return theme
}

// テーマコンテキストの型定義
type ThemeContextType = {
  toggleColorMode: () => void
  mode: ThemeMode
}

// テーマコンテキストの作成
export const ThemeContext = React.createContext<ThemeContextType>({
  toggleColorMode: () => {},
  mode: 'light',
})

// テーマコンテキストを使用するためのフック
export const useAppTheme = () => {
  const context = React.useContext(ThemeContext)

  if (context === undefined) {
    throw new Error('useAppTheme must be used within a ThemeProvider')
  }

  return context
}

// デフォルトテーマをエクスポート
export const defaultTheme = createAppTheme('light')
